/**
 * TemplateFillPanel — formulário de preenchimento das variáveis de um
 * template Meta (WhatsApp Business), compartilhado pelo drawer de mensagens
 * prontas do inbox e pelo modal de envio da TemplatesPage.
 *
 * Com template_params_ui (2026-09-14) usa o editor único
 * (TemplateParamsEditor): "O que entra aqui?" por variável, cabeçalho de mídia,
 * botões e prévia resolvida para o contato. Sem a flag, o formulário antigo,
 * idêntico ao de antes.
 *
 * Regras:
 * - {{1}} do corpo vem pré-preenchido com o primeiro nome do contato quando
 *   conhecido (editável).
 * - Os valores aceitam variáveis de personalização ({{primeiro_nome}},
 *   {{campo:Nome}}) — o backend resolve por destinatário e BLOQUEIA quando
 *   resolver vazio.
 * - Enviar fica desabilitado enquanto houver campo em branco (a mensagem
 *   nunca sai com buraco).
 *
 * onSend(template, extra) recebe o payload pronto para
 * templateService.send(name, phone, extra).
 */
import React, { useMemo, useState } from 'react';
import { Loader2, Send } from 'lucide-react';
import {
  extractTemplateParams, fillBodyText, looksLikePhone,
} from '../../utils/templateVariables';
import {
  analyzeTemplate, buildSendExtra, emptyTemplateValues, missingValues, CAPS_FULL,
} from '../../utils/templateParams';
import useFeatureFlags from '../../hooks/useFeatureFlags';
import TemplateParamsEditor from './TemplateParamsEditor';

const PanelButtons = ({ colors, sending, missing, onCancel, onSend }) => (
  <div style={{ display: 'flex', gap: 8 }}>
    <button
      onClick={onCancel}
      style={{
        flex: 1, padding: '8px 12px', borderRadius: 8,
        border: `1px solid ${colors.borderColor}`, background: 'transparent',
        color: colors.textSecondary, fontSize: 12, fontWeight: 600, cursor: 'pointer',
      }}
    >
      Cancelar
    </button>
    <button
      onClick={onSend}
      disabled={missing.length > 0 || sending}
      title={missing.length > 0 ? `Falta preencher: ${missing.join(', ')}` : 'Enviar template'}
      style={{
        flex: 2, padding: '8px 12px', borderRadius: 8, border: 'none',
        backgroundColor: missing.length > 0 ? colors.bgTertiary : colors.accentPrimary,
        color: missing.length > 0 ? colors.textTertiary : '#fff',
        fontSize: 12, fontWeight: 600,
        cursor: missing.length > 0 || sending ? 'not-allowed' : 'pointer',
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6,
      }}
    >
      {sending ? (
        <>
          <Loader2 size={12} style={{ animation: 'spin 1s linear infinite' }} />
          Enviando…
        </>
      ) : (
        <>
          <Send size={12} /> Enviar preenchido
        </>
      )}
    </button>
  </div>
);

const TemplateFillPanelEditor = ({
  template, colors, contactName, contactPhone, sending, onSend, onCancel,
}) => {
  const analysis = useMemo(() => analyzeTemplate(template), [template]);
  const temNome = Boolean(contactName && !looksLikePhone(contactName));
  const [values, setValues] = useState(() => {
    const init = emptyTemplateValues();
    if (analysis.body.params.includes(1) && temNome) init.body_params = ['{{primeiro_nome}}'];
    return init;
  });
  const missing = missingValues(analysis, values, CAPS_FULL);

  return (
    <div style={{
      marginTop: 8, paddingTop: 10, borderTop: `1px dashed ${colors.borderColor}`,
      display: 'flex', flexDirection: 'column', gap: 10,
    }}>
      <TemplateParamsEditor
        template={template}
        value={values}
        onChange={setValues}
        capabilities={CAPS_FULL}
        colors={colors}
        mode="single"
        contactKey={contactPhone || ''}
        contactName={contactName || ''}
      />
      {missing.length > 0 && (
        <div style={{ fontSize: 11, color: colors.textTertiary }}>{missing[0]}</div>
      )}
      <PanelButtons
        colors={colors}
        sending={sending}
        missing={missing}
        onCancel={onCancel}
        onSend={() => onSend(template, buildSendExtra(analysis, values))}
      />
    </div>
  );
};

const TemplateFillPanelLegado = ({ template, colors, contactName, sending, onSend, onCancel }) => {
  const info = useMemo(() => extractTemplateParams(template), [template]);
  const firstName = contactName && !looksLikePhone(contactName)
    ? contactName.trim().split(/\s+/)[0]
    : '';
  const [bodyValues, setBodyValues] = useState(() =>
    info.bodyParams.map((n) => (n === 1 && firstName ? firstName : ''))
  );
  const [headerValue, setHeaderValue] = useState('');
  const [buttonValues, setButtonValues] = useState({});

  const missing = [];
  info.bodyParams.forEach((n, i) => {
    if (!(bodyValues[i] || '').trim()) missing.push(`{{${n}}} do corpo`);
  });
  if (info.header?.needsMedia && !headerValue.trim()) {
    missing.push(`mídia do cabeçalho (${info.header.format.toLowerCase()})`);
  } else if (info.header?.params?.length && !headerValue.trim()) {
    missing.push('{{1}} do cabeçalho');
  }
  info.buttons.forEach((b) => {
    if (!((buttonValues[b.index] || '').trim())) missing.push(`botão ${b.index + 1}`);
  });

  const preview = fillBodyText(info.bodyText, bodyValues);

  const handleSend = () => {
    const extra = {};
    if (info.bodyParams.length) extra.body_params = bodyValues.map((v) => (v || '').trim());
    if (info.header?.needsMedia) {
      extra.header_param = { type: info.header.format.toLowerCase(), link: headerValue.trim() };
    } else if (info.header?.params?.length) {
      extra.header_param = { type: 'text', text: headerValue.trim() };
    }
    if (info.buttons.length) {
      extra.button_params = {};
      info.buttons.forEach((b) => {
        extra.button_params[String(b.index)] = (buttonValues[b.index] || '').trim();
      });
    }
    onSend(template, extra);
  };

  const inputStyle = {
    width: '100%', padding: '7px 10px', borderRadius: 8,
    border: `1px solid ${colors.borderColor}`,
    backgroundColor: colors.bgPrimary, color: colors.textPrimary,
    fontSize: 12, outline: 'none', boxSizing: 'border-box',
  };
  const labelStyle = {
    fontSize: 11, fontWeight: 600, color: colors.textSecondary,
    marginBottom: 4, display: 'block',
  };

  return (
    <div style={{
      marginTop: 8, paddingTop: 10, borderTop: `1px dashed ${colors.borderColor}`,
      display: 'flex', flexDirection: 'column', gap: 10,
    }}>
      {info.header?.needsMedia && (
        <div>
          <label style={labelStyle}>
            Mídia do cabeçalho ({info.header.format.toLowerCase()}): link público
          </label>
          <input
            style={inputStyle}
            value={headerValue}
            onChange={(e) => setHeaderValue(e.target.value)}
            placeholder="https://…"
          />
        </div>
      )}
      {!info.header?.needsMedia && info.header?.params?.length > 0 && (
        <div>
          <label style={labelStyle}>Cabeçalho: {'{{1}}'}</label>
          <input
            style={inputStyle}
            value={headerValue}
            onChange={(e) => setHeaderValue(e.target.value)}
          />
        </div>
      )}
      {info.bodyParams.map((n, i) => (
        <div key={n}>
          <label style={labelStyle}>Variável {`{{${n}}}`} do corpo</label>
          <input
            style={inputStyle}
            value={bodyValues[i] || ''}
            onChange={(e) => setBodyValues((prev) => {
              const next = [...prev];
              next[i] = e.target.value;
              return next;
            })}
          />
        </div>
      ))}
      {info.buttons.map((b) => (
        <div key={b.index}>
          <label style={labelStyle}>
            Botão {b.index + 1} ({b.subType === 'copy_code' ? 'código' : 'complemento do link'})
          </label>
          <input
            style={inputStyle}
            value={buttonValues[b.index] || ''}
            onChange={(e) => setButtonValues((prev) => ({ ...prev, [b.index]: e.target.value }))}
            placeholder={b.subType === 'url' ? b.url.replace('{{1}}', '…') : ''}
          />
        </div>
      ))}
      <div style={{ fontSize: 10, color: colors.textTertiary }}>
        Os campos aceitam variáveis como {'{{primeiro_nome}}'} e {'{{campo:Nome}}'},
        resolvidas para este contato no envio.
      </div>
      <div>
        <div style={{ ...labelStyle, marginBottom: 4 }}>Prévia</div>
        <div style={{
          fontSize: 12, color: colors.textPrimary, whiteSpace: 'pre-wrap',
          backgroundColor: colors.bgPrimary, borderRadius: 8, padding: '8px 10px',
          border: `1px solid ${colors.borderColor}`, lineHeight: 1.4,
        }}>
          {preview}
        </div>
      </div>
      <PanelButtons
        colors={colors}
        sending={sending}
        missing={missing}
        onCancel={onCancel}
        onSend={handleSend}
      />
    </div>
  );
};

const TemplateFillPanel = (props) => {
  const { flags } = useFeatureFlags();
  return flags.template_params_ui
    ? <TemplateFillPanelEditor {...props} />
    : <TemplateFillPanelLegado {...props} />;
};

export default TemplateFillPanel;
